/**
 * Form values and rules of the fabric dialog, the CSV import mapping and the
 * columns of the fabrics screen.
 */
import { getImage } from '@/shared/lib/image';
import { z } from 'zod';
import type { DmsItem } from '../api';

/** `data` of a fabric item; the fields the iPad app adds are kept (round trip). */
export const fabricDataSchema = z
  .object({
    productLine: z.string().nullish(),
    book: z.string().nullish(),
    code: z.string().nullish(),
    description: z.string().nullish(),
    order: z.union([z.number(), z.string()]).nullish(),
    weight: z.string().nullish(),
    weightGroup: z.string().nullish(),
    titrage: z.string().nullish(),
    composition: z.string().nullish(),
    compositionGroup: z.string().nullish(),
    armure: z.string().nullish(),
    pattern: z.string().nullish(),
    colorGroup: z.string().nullish(),
    resupplyDate: z.string().nullish(),
    availablePermanent: z.boolean().nullish(),
    availableVillaDior: z.boolean().nullish(),
    lowStock: z.boolean().nullish(),
    eveningOffer: z.boolean().nullish(),
    picture: z.string().nullish(),
    /** Never set by the API on `data` (it adds it on the item): see migration.md. */
    displayAvailability: z.string().nullish(),
  })
  .loose();
export type FabricData = z.infer<typeof fabricDataSchema>;

export type DmsFabric = DmsItem<FabricData>;

export const FABRIC_TEXT_FIELDS = [
  'code',
  'description',
  'weight',
  'weightGroup',
  'titrage',
  'composition',
  'compositionGroup',
  'armure',
  'pattern',
  'colorGroup',
  'resupplyDate',
] as const;

export interface FabricFormValues {
  productLine: string;
  book: string;
  code: string;
  description: string;
  order: string;
  weight: string;
  weightGroup: string;
  titrage: string;
  composition: string;
  compositionGroup: string;
  armure: string;
  pattern: string;
  colorGroup: string;
  resupplyDate: string;
  availablePermanent: boolean;
  availableVillaDior: boolean;
  lowStock: boolean;
  eveningOffer: boolean;
}

export type FabricFormErrors = Partial<Record<keyof FabricFormValues, string>>;

export const fabricFormValues = (
  item: DmsFabric | null,
  productLine: string | null,
): FabricFormValues => ({
  productLine: item?.data.productLine ?? productLine ?? '',
  book: item?.data.book ?? '',
  code: item?.data.code ?? '',
  description: item?.data.description ?? '',
  order: item?.data.order == null ? '' : String(item.data.order),
  weight: item?.data.weight ?? '',
  weightGroup: item?.data.weightGroup ?? '',
  titrage: item?.data.titrage ?? '',
  composition: item?.data.composition ?? '',
  compositionGroup: item?.data.compositionGroup ?? '',
  armure: item?.data.armure ?? '',
  pattern: item?.data.pattern ?? '',
  colorGroup: item?.data.colorGroup ?? '',
  resupplyDate: item?.data.resupplyDate ?? '',
  availablePermanent: !!item?.data.availablePermanent,
  availableVillaDior: !!item?.data.availableVillaDior,
  lowStock: !!item?.data.lowStock,
  eveningOffer: !!item?.data.eveningOffer,
});

export const validateFabricForm = (
  values: FabricFormValues,
): FabricFormErrors => {
  const errors: FabricFormErrors = {};
  if (!values.productLine) errors.productLine = 'Wrong product line';
  if (!values.book) errors.book = 'Wrong fabric book';
  if (!values.code.trim()) errors.code = 'Wrong fabric code';
  const order = values.order.trim() === '' ? Number.NaN : Number(values.order);
  if (Number.isNaN(order) || order < 0) errors.order = 'Wrong order';
  return errors;
};

/** Every control (the titrage / weight not shown included), the order as a number. */
export const formToFabricData = (
  values: FabricFormValues,
  now: number,
): FabricData => ({
  ...values,
  order: values.order.trim() === '' ? null : Number(values.order),
  mainPictureUpdatedAt: now,
});

/** The picture path names its bucket. */
export const fabricPictureUrl = (item: DmsFabric | null): string | null => {
  const path = item?.data.picture;
  if (!path) return null;
  if (path.includes('dms-front-assets'))
    return getImage('dms', undefined, path);
  if (path.includes('abcdior-back')) return getImage('abc', undefined, path);
  return null;
};

/** Columns of the list; `sortBy` is the API field. */
export const FABRIC_COLUMNS: {
  key: keyof FabricData;
  label: string;
  width: string;
  sortBy?: string;
}[] = [
  {
    key: 'productLine',
    label: 'Product Line',
    width: '8%',
    sortBy: 'data.productLine',
  },
  { key: 'book', label: 'Fabric Book', width: '8%', sortBy: 'data.book' },
  { key: 'code', label: 'Fabric Code', width: '8%', sortBy: 'data.code' },
  {
    key: 'description',
    label: 'Description',
    width: '8%',
    sortBy: 'data.description',
  },
  { key: 'order', label: 'Order', width: '5%', sortBy: 'data.order' },
  { key: 'titrage', label: 'Titrage', width: '5%', sortBy: 'data.titrage' },
  { key: 'weight', label: 'Weight', width: '8%', sortBy: 'data.weight' },
  {
    key: 'weightGroup',
    label: 'Weight Group',
    width: '10%',
    sortBy: 'data.weightGroup',
  },
  {
    key: 'composition',
    label: 'Composition',
    width: '10%',
    sortBy: 'data.composition',
  },
  {
    key: 'compositionGroup',
    label: 'Composition Group',
    width: '12%',
    sortBy: 'data.compositionGroup',
  },
  { key: 'armure', label: 'Armure', width: '7%', sortBy: 'data.armure' },
  { key: 'pattern', label: 'Pattern', width: '7%', sortBy: 'data.pattern' },
  {
    key: 'colorGroup',
    label: 'Color Group',
    width: '8%',
    sortBy: 'data.colorGroup',
  },
  {
    key: 'resupplyDate',
    label: 'Resupply Date',
    width: '8%',
    sortBy: 'data.resupplyDate',
  },
  { key: 'displayAvailability', label: 'Availability', width: '7%' },
];

/** The field changes and the order flips, whatever the field was. */
export const nextFabricSort = (
  current: { by: string | null; ascending: boolean },
  by: string,
): { by: string; ascending: boolean } => ({
  by,
  ascending: !current.ascending,
});

/** Date of the last CSV upload as `DD/MM/YYYY HH:mm`, N/A when invalid. */
export const formatCsvDate = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** File name of the export: `fabrics-DDMMYYYY.csv`. */
export const csvExportFileName = (now: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `fabrics-${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}.csv`;
};

const CSV_PRODUCT_LINE_COLUMNS: Record<string, string> = {
  productLineShirt: 'SHIRT',
  productLineSuit: 'SUIT',
  productLineJacket: 'JACKET',
  productLinePants: 'PANTS',
  productLineWaistCoat: 'WAISTCOAT',
  productLineMorningSuit: 'MORNING_SUIT',
  productLineCoat: 'COAT',
};

const isYes = (value: unknown): boolean =>
  String(value ?? '')
    .toUpperCase()
    .replace(/ /g, '') === 'YES';

/**
 * One item per product line column set to "yes", the
 * availability columns turned into booleans (a missing product line column
 * counts as "no").
 */
export const csvRowsToFabrics = (
  rows: readonly Record<string, unknown>[],
): { type: 'fabric'; data: Record<string, unknown> }[] =>
  rows.flatMap((row) => {
    const rest: Record<string, unknown> = { ...row };
    const lines: string[] = [];
    for (const [column, line] of Object.entries(CSV_PRODUCT_LINE_COLUMNS)) {
      if (isYes(rest[column])) lines.push(line);
      delete rest[column];
    }
    return lines.map((productLine) => ({
      type: 'fabric' as const,
      data: {
        ...rest,
        availablePermanent: isYes(rest.availablePermanent),
        availableVillaDior: isYes(rest.availableVillaDior),
        eveningOffer: isYes(rest.eveningOffer),
        productLine,
      },
    }));
  });
